import { AsyncPipe, NgClass } from "@angular/common";
import {
    AfterContentChecked,
    Component,
    EventEmitter,
    Input,
    NgZone,
    OnDestroy,
    OnInit,
    Output,
    ViewChild,
    inject,
    ChangeDetectionStrategy
} from "@angular/core";
import { TabComponent } from "@components/tabs/tab/tab.component";
import { TabsComponent } from "@components/tabs/tabs.component";
import { TogglePanelComponent as TogglePanelComponent_1 , TogglePanelComponent } from "@components/toggle-panel/toggle-panel.component";
import _ from "lodash";
import {
    Observable,
    Subject,
    Subscription,
    firstValueFrom,
    of
} from "rxjs";
import {
    filter,
    switchMap,
    takeUntil,
    tap,
    map
} from "rxjs/operators";
import { SpatialService } from "~app/common/components/map/services/spatial.service";

import { Atgardstyp } from "~app/common/models/constants";
import { EntityDTO } from "~app/common/models/entity-models";
import { EntityType } from "~app/common/services/cqs/models/rule-validation-message";
import { FeatureService } from "~app/common/services/feature.service";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { Atgard, VirkeskopsatgardKompatibilitet } from "~app/features/virkeskop/models/atgard-models";
import { Geometri } from "../../models/karta-models";
import { AtgardMediatorService } from "../../services/atgard-mediator.service";
import { AvtalService } from "../../services/avtal.service";
import { AvverkningsanmalanMediatorService } from "../../services/avverkningsanmalan-mediator.service";
import { AvverkningsanmalanService } from "../../services/avverkningsanmalan.service";
import { KontraktMediatorService } from "../../services/kontrakt-mediator.service";
import { NavigationService } from "../../services/navigation.service";
import { NvbMediatorService } from "../../services/nvb-mediator.service";
import { TradslagMediatorService } from "../../services/tradslag-mediator.service";
import { AtgardClientValidationService } from "../../services/validation/atgard-client-validation.service";
import { BeraknatUtbyteValidationService } from "../../services/validation/beraknat-utbyte-validation.service";
import { VirkeskopValidationService } from "../../services/validation/virkeskop-validation.service";
import { VkKartaMediatorService } from "../../services/vk-karta-mediator.service";
import { VirkeskopValidationMessageComponent } from "../../validation/validation-message/virkeskop-validation-message.component";
import { AtgardAvverkningsanmalanSamradComponent } from "../atgard-avverkningsanmalan-samrad/atgard-avverkningsanmalan-samrad.component";
import { AtgardAvverkningsanmalanComponent } from "../atgard-avverkningsanmalan/atgard-avverkningsanmalan.component";
import { AtgardDetaljerComponent } from "../atgard-detaljer/atgard-detaljer.component";
import { AtgardForkalkylComponent } from "../atgard-forkalkyl/atgard-forkalkyl.component";
import { AtgardHeaderComponent } from "../atgard-header/atgard-header.component";
import { AtgardMbfComponent } from "../atgard-mbf/atgard-mbf.component";
import { AtgardNvbComponent } from "../atgard-nvb/atgard-nvb.component";
import { AtgardStatusComponent } from "../atgard-status/atgard-status.component";
import { AtgardTransportComponent } from "../atgard-transport/atgard-transport.component";
import { AtgardVolymComponent } from "../atgard-volym/atgard-volym.component";
import { canEdit } from "~app/role-permissions";
@Component({
    selector: "rask-atgard-item",
    templateUrl: "./atgard-item.component.html",
    styleUrls: ["./atgard-item.component.scss"],
    providers: [
        AtgardMediatorService,
        AvverkningsanmalanMediatorService,
        NvbMediatorService,
        TradslagMediatorService,
        AtgardClientValidationService,
        BeraknatUtbyteValidationService
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        TogglePanelComponent_1,
        NgClass,
        AtgardHeaderComponent,
        VirkeskopValidationMessageComponent,
        TabsComponent,
        TabComponent,
        AtgardDetaljerComponent,
        AtgardVolymComponent,
        AtgardForkalkylComponent,
        AtgardNvbComponent,
        AtgardMbfComponent,
        AtgardAvverkningsanmalanComponent,
        AtgardTransportComponent,
        AtgardAvverkningsanmalanSamradComponent,
        AtgardStatusComponent,
        AsyncPipe
    ]
})
export class AtgardItemComponent implements OnInit, OnDestroy, AfterContentChecked {
    highlight: boolean;
    @Input() index: number;
    @Output() loading = new EventEmitter<boolean>();
    @ViewChild(TogglePanelComponent) togglePanel!: TogglePanelComponent;

    protected atgard: Atgard;
    protected isReadonly$: Observable<boolean>;
    protected tab: any;

    private readonly atgardClientValidationService = inject(AtgardClientValidationService);
    private readonly atgardMediatorService = inject(AtgardMediatorService);
    private readonly avtalService = inject(AvtalService);
    private readonly avverkningsanmalanService = inject(AvverkningsanmalanService);
    private readonly beraknatUtbyteValidationService = inject(BeraknatUtbyteValidationService);
    private readonly featureService = inject(FeatureService);
    private readonly kontraktMediatorService = inject(KontraktMediatorService);
    private readonly navigationService = inject(NavigationService);
    private readonly ngUnsubscribe$ = new Subject<void>();
    private readonly ngZone = inject(NgZone);
    private readonly spatialService = inject(SpatialService);
    private readonly tradslagMediatorService = inject(TradslagMediatorService);
    private readonly validationService = inject(VirkeskopValidationService);
    private readonly vkKartaMediatorService = inject(VkKartaMediatorService);

    private _entity: EntityDTO;
    private navigationSubscription: Subscription;

    private readonly canEdit = canEdit();

    constructor() {
        this.isReadonly$ = this.kontraktMediatorService.isReadonly$.pipe(
            map(readonly => readonly || !this.canEdit())
        );
    }

    @Input()
    set entity(entity: EntityDTO) {
        this._entity = entity;
        this.atgardMediatorService.initAtgard(entity);
    }

    get isMarkberedForyngAtgActive(): boolean {
        return this.atgard?.id &&
            Atgardstyp.markberedningbForyngringTyper.some(x => x === this.atgard.atgardstyp);
    }

    get kontrakt$() {
        return this.kontraktMediatorService.kontrakt$;
    }

    canHandleAvverkningsanmalan(): boolean {
        return this.avverkningsanmalanService.canHandleAvverkningsanmalan(this.atgard);
    }

    getIcon(entity: EntityType): string {
        return this.validationService.getEntitySeverity(entity, this.atgard.guid);
    }

    isAnmSamRequired() {
        return !this.atgard.hasKompatibilitet(VirkeskopsatgardKompatibilitet.LeveransvirkeInnanGeometri);
    }

    ngAfterContentChecked(): void {
        if (!this.togglePanel || this.navigationSubscription) {
            return;
        }

        this.navigationSubscription = this.navigationService.atgardId$
            .subscribe((atgardId) => {
                if (this.togglePanel && atgardId) {
                    this.togglePanel.forceExpandedState(atgardId === this._entity.id);
                }
            });
    }

    ngOnDestroy(): void {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
        if (this.navigationSubscription) {
            this.navigationSubscription.unsubscribe();
        }
    }

    ngOnInit(): void {
        this.atgardClientValidationService.initSubscriptions();
        this.beraknatUtbyteValidationService.initSubscriptions();
        this.atgardMediatorService.atgard$.pipe(
            takeUntil(this.ngUnsubscribe$)
        ).subscribe(atgard => this.atgard = atgard);

        this.vkKartaMediatorService.atgardGeometriChanged$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(geometri => {
                this.updateGeometri(geometri);
                if (geometri?.kartgeometri) {
                    this.tradslagMediatorService.mapDirektutbyteFromImport(geometri, this._entity.guid);
                }
            });

        this.vkKartaMediatorService.atgardGeometriDeleted$
            .pipe(takeUntil(this.ngUnsubscribe$), takeUntil(this.atgardMediatorService.removed$))
            .subscribe(geometri => this.updateGeometri(geometri));
        this.vkKartaMediatorService.atgardHoverIn$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(guid => this.setHighlight(this._entity.guid === guid));
        this.vkKartaMediatorService.atgardHoverOut$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(guid => this.setHighlight(false));

        // Tradslag
        const atgardId = this._entity?.id;
        const getSnabbkalkyl = firstValueFrom(!_.isNil(atgardId)
            ? this.avtalService.getSnabbkalkyl(atgardId)
            : of(null));
        const getDirektutbyte = firstValueFrom(!_.isNil(atgardId)
            ? this.avtalService.getDirektutbyte(atgardId)
            : of(null));

        this.vkKartaMediatorService.atgardGeometriChanged$
            .pipe(
                filter(k => k && this.atgard && k.atgardGuid === this.atgard.guid),
                switchMap(x => this.avtalService.getVirkeskopsatgardExtraKartInfo(this.atgard.id, x.kartgeometri.geometri)),
                filter(res => !!res),
                tap(res => this.atgardMediatorService.updateExtraKartInfo(res)),
                takeUntil(this.ngUnsubscribe$))
            .subscribe();

        this.tradslagMediatorService.init(getSnabbkalkyl, getDirektutbyte);
    }

    onPlaneringsstatusChange(value: string) {
        if (this.atgard.status === value) {
            return;
        }

        this.atgard.status = value;
        this.atgardMediatorService.update(this.atgard);
    }

    showAtgardNVBTab(): boolean {
        return !ObjectUtils.isNullOrUndefined(this.atgard.centrumKoordinat) &&
            this.atgard.id &&
            (ObjectUtils.isNullOrUndefined(this.atgard.nvBedomningUtfordDatum) || !this.atgard.nvBedomningUtfordDatum);
    }

    private setHighlight(value: boolean) {
        this.ngZone.run(() => {
            this.highlight = value;
        });
    }

    private updateGeometri(geometri: Geometri): void {
        if (geometri?.atgardGuid === this._entity.guid) {
            if (ObjectUtils.isNullOrUndefined(geometri?.mapGeometry?.wkt)) {
                // atgardsgeometrin borttagen
                this.atgard.nettoareal = 0;
                this.atgard.geometri = null;
                if (!this.atgard.isLeveransvirke) {
                    this.atgard.centrumKoordinat = null;
                }
            } else {
                this.atgard.nettoareal = geometri?.areal;
                this.atgard.geometri = geometri?.mapGeometry?.wkt;
                if (!this.atgard.isLeveransvirke) {
                    this.atgard.centrumKoordinat = this.spatialService.getCenterPointAsWkt(geometri?.mapGeometry);
                }
            }
            this.atgardMediatorService.update(this.atgard);
        }
    }
}
